import React, { useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import DeleteForever from '@mui/icons-material/DeleteForever';
import { toast } from 'react-toastify';
import api from '../services/api';

interface Props {
  profile: { user_id: number; profile_id?: string; name: string };
  onClose: () => void;
  onDeleted: () => Promise<void>;
}

const PendingProfileDeleteDialog: React.FC<Props> = ({ profile, onClose, onDeleted }) => {
  const [confirmation, setConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);
  const profileId = profile.profile_id || String(profile.user_id);

  const deleteProfile = async () => {
    if (confirmation.trim() !== profileId || deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/users/admin/pending-profiles/${profile.user_id}`);
      toast.success('Profile and associated local images permanently deleted');
      await onDeleted();
      onClose();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete profile');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open onClose={() => !deleting && onClose()} maxWidth="sm" fullWidth disableEscapeKeyDown={deleting}>
      <DialogTitle>Permanently Delete Profile</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>This cannot be undone. The account, profile, local images, preferences, sessions and associated records will be removed.</Alert>
        <Typography sx={{ mb: 2 }}>{profile.name} ({profileId})</Typography>
        <TextField fullWidth label={`Type ${profileId} to confirm`} value={confirmation} onChange={(event) => setConfirmation(event.target.value)} disabled={deleting} autoComplete="off" />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>Cancel</Button>
        <Button color="error" variant="contained" startIcon={<DeleteForever />} onClick={() => void deleteProfile()} disabled={deleting || confirmation.trim() !== profileId}>
          {deleting ? 'Deleting...' : 'Delete Permanently'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PendingProfileDeleteDialog;